import type { BondChip } from "@/lib/bondWizard";
import type { BondOwner, BondPreapproval } from "@/lib/brokerBonds";
import { readFor } from "@/lib/bondRoster";

// BRK-S197 — editing Business and Indemnitor after the Checks have run.
//
// THE RULE (Aaron, 2026-10-07; Sprint 10 rule 20): editing after checks must
// not leave a pre-approval standing on data it was not decided on. Before
// submit, the fields steps are reachable again. Saving a change to a field the
// bureau pulls use — the PRIMARY indemnitor's first/last name, SSN, date of
// birth or home address; the business legal name, EIN or address — marks the
// checks STALE: the decision is withdrawn from the confirmation path and the
// checks run again, with a notice that this is a new credit check. Any other
// change saves without re-running. After submit nothing is editable.
//
// ⚠️ DERIVED CLIENT-SIDE, PER SESSION. Neither PMS nor surety refuses a step
// save after a settled run, and the order view publishes no record of the
// inputs a run was decided on — so the comparison is the SAVE's: what was on
// the order before it against what the save sent. A reload after a stale save
// but before the re-run would replay the old run; PMS-S736 (filed with this
// story) publishes the stale state so that window closes.

/** Business paths the bureau pulls use. */
export const PULL_BUSINESS_PATHS: readonly string[] = [
  "business.legalName",
  "business.ein",
  "business.address",
];

/** The PRIMARY indemnitor's paths the bureau pulls use. */
export const PULL_PRIMARY_PATHS: readonly string[] = [
  "personal[].firstName",
  "personal[].lastName",
  "personal[].ssn",
  "personal[].birthDate",
  "personal[].address",
];

/** Write-only PII: PMS redacts it from every echo, so a value SENT is a value
 *  the broker typed — there is no stored copy to compare it with. */
const WRITE_ONLY = new Set(["personal[].ssn"]);

const same = (a: unknown, b: unknown) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Did this save change a pull-relevant value?
 *
 * `before` is the order's own bag for the step (contract spelling), `sent` the
 * save's body, `primaryOwnerId` the primary the save declared. A path the save
 * did not send is unchanged; a write-only path the save DID send counts as a
 * change, because nothing on the client can say it matches what is held.
 */
export function pullInputsChanged({
  before,
  sent,
  primaryOwnerId,
  owners,
}: {
  before: Readonly<Record<string, unknown>>;
  sent: Readonly<Record<string, unknown>>;
  primaryOwnerId: string | undefined;
  owners: readonly BondOwner[] | undefined;
}): boolean {
  for (const path of PULL_BUSINESS_PATHS) {
    if (path in sent && !same(sent[path], before[path])) return true;
  }
  if (primaryOwnerId === undefined) {
    // Not a roster step: its one set of answers IS the primary's, held as
    // collapsed scalars under the contract path.
    for (const path of PULL_PRIMARY_PATHS) {
      const now = sent[path];
      if (now === undefined || now === "") continue;
      if (WRITE_ONLY.has(path)) return true;
      if (!same(now, before[path])) return true;
    }
    return false;
  }
  // The sent bag's entries are keyed by owner; a collapsed scalar in `before`
  // is the declared primary's (`readFor`).
  const sentOwners: BondOwner[] = [
    { ownerId: primaryOwnerId, displayName: "", isPrimary: true },
  ];
  for (const path of PULL_PRIMARY_PATHS) {
    const now = readFor(sent, path, primaryOwnerId, sentOwners);
    if (now === undefined || now === "") continue;
    if (WRITE_ONLY.has(path)) return true;
    if (!same(now, readFor(before, path, primaryOwnerId, owners))) return true;
  }
  return false;
}

/**
 * The decision the stale checks produced, kept so it can be recognised —
 * by its SERVER timestamp, never the client's clock.
 */
export interface StaleDecision {
  evaluatedAt: string | null;
}

/**
 * This order's decision, or null while it is one the stale checks made: a
 * decision evaluated no later than the stale one was decided on data the
 * broker has since changed, so it may not reach the confirmation path. A
 * standing (account) program is not this order's checks and is left alone.
 */
export function withoutStaleDecision(
  preapproval: BondPreapproval | null,
  stale: StaleDecision | null,
): BondPreapproval | null {
  if (stale === null || preapproval === null) return preapproval;
  if (preapproval.source === "account") return preapproval;
  if (preapproval.status === "pending") return preapproval;
  // An untimestamped `not_granted` is PMS folding an undecided run (PMS-S312 /
  // PMS-S736). After a REAL decision went stale it can only be the re-run's
  // answer, and comparing its missing `evaluatedAt` as "" discarded it and
  // stalled the chip (SB-01628). But when the stale decision was ITSELF an
  // undecided not_granted, PMS answers with that same one until the re-run
  // starts, so it proves nothing and the chip must keep reading (SB-01629: the
  // approval that followed was missed).
  if (
    preapproval.status === "not_granted" &&
    preapproval.evaluatedAt == null &&
    stale.evaluatedAt != null
  ) {
    return preapproval;
  }
  return (preapproval.evaluatedAt ?? "") > (stale.evaluatedAt ?? "")
    ? preapproval
    : null;
}

/**
 * May the step bar take the broker back to this chip? Only a DRAFT, only a
 * chip already behind them, and only a fields step — the Checks and the
 * Indication are results, not forms, and eligibility is settled for good once
 * the order exists.
 */
export function canRevisitChip(
  chip: BondChip,
  index: number,
  current: number,
  draft: boolean,
): boolean {
  return (
    draft &&
    index >= 1 &&
    index < current &&
    chip.kind === "step" &&
    chip.step.type === "fields"
  );
}

export const NEW_CREDIT_CHECK_NOTICE =
  "You changed details the credit check uses, so a new credit check is running. Your earlier result no longer applies.";
